import { useCallback, useEffect, useState } from 'react';

import { useLastDifferentValue } from 'src/hooks';
import { Session } from 'src/models/Session';
import { Challenge, ChallengeSession } from 'src/models/Challenge';
import { BackNavigationInfo } from 'src/models/BackNavigationInfo';
import { SessionsService } from 'src/services';
import {
  PlaylistActionsMenu,
  canShowPlaylistOnListing,
  resolvePlaylistContentType,
} from 'src/features/AddToPlaylist';

import { SessionCardActual } from './SessionCardActual';
import {
  TransitionContainer,
  Skeleton,
  TransitionItem,

} from './styles/transition';
import {
  composeLink,
  resolveDuration,
  resolveImage,
  resolveOverlineLabel,
  resolveTypeName,
  toggleLike,
} from './helpers';

export const SessionCard: React.FC<{
  mode: 'horizontal' | 'vertical';
  pathBase?: string;
  session?: Session | Challenge | ChallengeSession;
  order?: number;
  backNavigationInfo?: BackNavigationInfo;
  onLikeToggleSuccess?: () => void;
}> = ({
  mode,
  pathBase,
  session,
  order = 0,
  backNavigationInfo,
  onLikeToggleSuccess,
}) => {
  const prevSession = useLastDifferentValue(session, (a, b) => a?.id === b?.id);

  const [isLiked, setIsLiked] = useState(false);
  useEffect(
    () => setIsLiked(session?.isLiked ?? false),
    [session],
  );

  const handleToggleLike = useCallback(() => {
    if (!session) return;
    toggleLike(session, !isLiked)
      .then(() => {
        setIsLiked(!isLiked);
        onLikeToggleSuccess?.();
      })
      // The error is already reported in the respective service. If it occurs,
      // we simply don't do anything at all UI-wise:
      .catch();
  }, [session, isLiked, onLikeToggleSuccess]);

  const [isPublicLiked, setIsPublicLiked] = useState(false);
  const [publicLikesCount, setPublicLikesCount] = useState(0);
  const [isPublicLikePending, setIsPublicLikePending] = useState(false);

  useEffect(() => {
    if (session?.contentType !== 'session') {
      setIsPublicLiked(false);
      setPublicLikesCount(0);
      return;
    }
    setIsPublicLiked(!!session.isLikedByUser);
    setPublicLikesCount(session.likesCount ?? 0);
  }, [session]);

  const handleTogglePublicLike = useCallback(() => {
    if (!session || session.contentType !== 'session' || isPublicLikePending) {
      return;
    }

    const previousLiked = isPublicLiked;
    const previousCount = publicLikesCount;
    const optimisticLiked = !previousLiked;
    const optimisticCount = Math.max(0, previousCount + (optimisticLiked ? 1 : -1));

    setIsPublicLiked(optimisticLiked);
    setPublicLikesCount(optimisticCount);
    setIsPublicLikePending(true);

    SessionsService.toggleSessionLike(session.id)
      .then((response) => {
        setIsPublicLiked(response?.liked ?? optimisticLiked);
        setPublicLikesCount(response?.likes_count ?? optimisticCount);
      })
      .catch(() => {
        setIsPublicLiked(previousLiked);
        setPublicLikesCount(previousCount);
      })
      .finally(() => {
        setIsPublicLikePending(false);
      });
  }, [session, isPublicLikePending, isPublicLiked, publicLikesCount]);

  const link = composeLink(session, backNavigationInfo, pathBase);

  const prevPlaylistMenu =
    prevSession && canShowPlaylistOnListing(prevSession, pathBase) ? (
    <PlaylistActionsMenu
      contentId={prevSession.id}
      contentType={resolvePlaylistContentType(prevSession, pathBase)}
      variant="listing"
    />
  ) : undefined;

  const playlistMenu =
    session && canShowPlaylistOnListing(session, pathBase) ? (
    <PlaylistActionsMenu
      contentId={session.id}
      contentType={resolvePlaylistContentType(session, pathBase)}
      variant="listing"
    />
  ) : undefined;

  return (
    <TransitionContainer key={order}>
      <Skeleton show={!session} mode={mode}/>
      {prevSession && (
        <TransitionItem
          order={order}
          mode="exit"
          key={`exit-${session?.id}`} // ensures exit animation will play again
        >
          <SessionCardActual
            contentType={prevSession.contentType}
            link="#"
            title={prevSession.title}
            underline={prevSession.underline}
            image={resolveImage(mode, prevSession)}
            overlineLabel={resolveOverlineLabel(prevSession)}
            type={resolveTypeName(prevSession)}
            coaches={prevSession.coach}
            difficulty={prevSession.difficulty}
            duration={resolveDuration(mode, prevSession)}
            mode={mode}
            isLiked={prevSession.isLiked}
            playlistMenu={prevPlaylistMenu}
          />
        </TransitionItem>
      )}
      {session && (
        <TransitionItem
          mode="enter"
          order={order}
          key={`enter-${session?.id}`}
        >
          <SessionCardActual
            contentType={session.contentType}
            link={link ?? ''}
            title={session.title}
            underline={session.underline}
            image={resolveImage(mode, session)}
            overlineLabel={resolveOverlineLabel(session)}
            type={resolveTypeName(session)}
            coaches={session.coach}
            difficulty={session.difficulty}
            duration={resolveDuration(mode, session)}
            mode={mode}
            isLiked={isLiked}
            onToggleLike={handleToggleLike}
            showPublicLike={session.contentType === 'session'}
            isPublicLiked={isPublicLiked}
            publicLikesCount={publicLikesCount}
            onTogglePublicLike={handleTogglePublicLike}
            publicLikePending={isPublicLikePending}
            playlistMenu={playlistMenu}
          />
        </TransitionItem>
      )}
    </TransitionContainer>
  );
};
